/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * Deterministic, client-side mock engines for every AI feature.
 *
 * Zero external calls, zero latency, zero cost. Used automatically by the axios
 * interceptor in whitelabel mode (see api.js). Each function is a pure JS
 * transform — safe to run 100 times a minute even on low-power tablets.
 *
 * These are INTENTIONALLY not "pretend" LLM outputs: they perform real,
 * useful, deterministic text structuring based on keywords and regex, so the
 * doctor always ends up with a usable draft even without a network.
 */

// ------------------------------------------------------------
// SOAP formatter — splits raw dictation into Subjective / Objective / Assessment / Plan.
// ------------------------------------------------------------

const SOAP_KEYWORDS = {
  subjective: [
    'paziente riferisce', 'riferisce', 'lamenta', 'dolore', 'sintomo', 'sintomi',
    'anamnesi', 'racconta', 'ha notato', 'da giorni', 'da settimane', 'da mesi',
    'sensazione', 'malessere', 'nausea', 'cefalea', 'fatica', 'stanchezza',
  ],
  objective: [
    'esame obiettivo', 'auscultazione', 'palpazione', 'pressione', 'frequenza cardiaca',
    'temperatura', 'saturazione', 'peso', 'altezza', 'bmi', 'fc', 'pa ', 'spo2',
    'toracico', 'addominale', 'edemi', 'reperto', 'rilevato', 'esame', 'obiettivamente',
    'mmhg', 'bpm', '°c', 'bpm', 'cm',
  ],
  assessment: [
    'diagnosi', 'sospetto', 'valutazione', 'probabile', 'compatibile con',
    'in diagnosi differenziale', 'ipotesi diagnostica', 'quadro clinico',
    'impressione', 'icd', 'k29', 'j45', 'i10',
  ],
  plan: [
    'prescrivo', 'prescrizione', 'terapia', 'consiglio', 'controllo', 'follow-up',
    'follow up', 'raccomando', 'suggerisco', 'invio', 'esame ematochimico',
    'ecografia', 'rx ', 'rmn', 'tac', 'appuntamento', 'rivalutazione', 'ricetta',
    'assumere', 'compresse', 'gocce', 'mg ', 'die', 'ogni ',
  ],
};

/**
 * Structure a raw clinical transcript into a SOAP note.
 * @param {string} rawText
 * @returns {{ subjective:string, objective:string, assessment:string, plan:string, formatted:string }}
 */
export function mockSoapFormat(rawText) {
  const text = String(rawText || '').trim();
  if (!text) {
    return { subjective: '', objective: '', assessment: '', plan: '', formatted: '' };
  }
  const sentences = text
    .split(/(?<=[.!?])\s+|\n+/g)
    .map((s) => s.trim())
    .filter(Boolean);

  const bucket = { subjective: [], objective: [], assessment: [], plan: [] };

  for (const s of sentences) {
    const lower = s.toLowerCase();
    const scores = {
      subjective: SOAP_KEYWORDS.subjective.filter((k) => lower.includes(k)).length,
      objective:  SOAP_KEYWORDS.objective.filter((k) => lower.includes(k)).length,
      assessment: SOAP_KEYWORDS.assessment.filter((k) => lower.includes(k)).length,
      plan:       SOAP_KEYWORDS.plan.filter((k) => lower.includes(k)).length,
    };
    const best = Object.entries(scores).reduce((a, b) => (b[1] > a[1] ? b : a), ['subjective', 0]);
    const target = best[1] > 0 ? best[0] : 'subjective';
    bucket[target].push(s);
  }

  const clean = (arr) => arr.join(' ').trim();
  const out = {
    subjective: clean(bucket.subjective),
    objective:  clean(bucket.objective),
    assessment: clean(bucket.assessment),
    plan:       clean(bucket.plan),
  };
  out.formatted = [
    `S — Soggettivo\n${out.subjective || '—'}`,
    `O — Obiettivo\n${out.objective || '—'}`,
    `A — Assessment\n${out.assessment || '—'}`,
    `P — Piano\n${out.plan || '—'}`,
  ].join('\n\n');
  return out;
}

// ------------------------------------------------------------
// Voice Diary structurer (OSS/nurse) — extracts vitals & observations.
// ------------------------------------------------------------

const NUM = '(\\d+(?:[\\.,]\\d+)?)';
const VITALS_PATTERNS = [
  { key: 'systolic_bp',  re: new RegExp(`(?:pa|pressione)\\s*(?:sistolica)?\\s*[:=]?\\s*${NUM}`, 'i'), unit: 'mmHg' },
  { key: 'diastolic_bp', re: new RegExp(`(?:diastolica)\\s*[:=]?\\s*${NUM}`, 'i'), unit: 'mmHg' },
  { key: 'heart_rate',   re: new RegExp(`(?:fc|battiti|bpm)\\s*[:=]?\\s*${NUM}`, 'i'), unit: 'bpm' },
  { key: 'temperature_c', re: new RegExp(`(?:temperatura|temp|tc)\\s*[:=]?\\s*${NUM}`, 'i'), unit: '°C' },
  { key: 'spo2',         re: new RegExp(`(?:sat|spo2|saturazione)\\s*[:=]?\\s*${NUM}`, 'i'), unit: '%' },
  { key: 'glucose_mg_dl', re: new RegExp(`(?:glicemia|glucosio)\\s*[:=]?\\s*${NUM}`, 'i'), unit: 'mg/dL' },
  { key: 'weight_kg',    re: new RegExp(`(?:peso)\\s*[:=]?\\s*${NUM}`, 'i'), unit: 'kg' },
];

const OBSERVATION_KEYWORDS = ['dolore', 'nausea', 'vomito', 'cefalea', 'vertigini',
  'insonnia', 'ansia', 'stipsi', 'diarrea', 'tosse', 'dispnea', 'edemi'];

export function mockVoiceDiaryStructure(transcript) {
  const text = String(transcript || '');
  if (!text.trim()) return { vitals: [], observations: [], summary: '' };

  const vitals = [];
  for (const p of VITALS_PATTERNS) {
    const m = text.match(p.re);
    if (m) {
      const value = parseFloat(m[1].replace(',', '.'));
      if (!Number.isNaN(value)) vitals.push({ metric: p.key, value, unit: p.unit });
    }
  }

  const observations = OBSERVATION_KEYWORDS
    .filter((k) => new RegExp(`\\b${k}\\b`, 'i').test(text))
    .map((label) => ({ label, present: true }));

  const summary = vitals.length
    ? `Rilevati ${vitals.length} parametri vitali e ${observations.length} osservazioni cliniche.`
    : 'Nessun parametro vitale rilevato. Testo salvato come nota libera.';

  return { vitals, observations, summary, raw: text };
}

// ------------------------------------------------------------
// Wound Care AI — deterministic template based on filename / metadata.
// ------------------------------------------------------------

export function mockWoundCareAnalysis({ filename = 'wound.jpg', notes = '' } = {}) {
  const isChronic = /chronic|cronica|ulcera|piaga/i.test(filename + ' ' + notes);
  return {
    stage: isChronic ? 'II-III' : 'I-II',
    tissue: isChronic
      ? 'Fondo con granulazione parziale, fibrina in quantità moderata'
      : 'Cute integra con eritema perilesionale, granulazione presente',
    exudate: isChronic ? 'moderato-abbondante' : 'scarso',
    infection_risk: isChronic ? 'medio' : 'basso',
    recommendations: [
      'Detergere con soluzione fisiologica sterile',
      'Applicare medicazione avanzata (idrocolloide o schiuma di poliuretano)',
      'Rivalutazione tra 48-72 ore',
      isChronic
        ? 'Considerare consulto chirurgico se non si osserva progressione a 7 giorni'
        : 'Educazione del paziente su asciugatura e igiene locale',
    ],
    _mock: true,
    _generated_at: new Date().toISOString(),
  };
}

// ------------------------------------------------------------
// Symptom checker triage — keyword-based severity scoring (NEWS2-inspired).
// ------------------------------------------------------------

const RED_FLAGS = ['dolore toracico', 'dispnea grave', 'perdita di coscienza', 'sanguinamento',
  'convulsioni', 'ictus', 'paralisi', 'confusione severa', 'labbra blu'];
const YELLOW_FLAGS = ['febbre alta', 'vomito ripetuto', 'diarrea persistente', 'cefalea intensa',
  'dolore addominale', 'palpitazioni', 'vertigini severe'];

export function mockSymptomTriage({ message = '', history = [] } = {}) {
  const allText = (history.map((m) => m.content || '').join(' ') + ' ' + message).toLowerCase();
  const red = RED_FLAGS.filter((k) => allText.includes(k));
  const yellow = YELLOW_FLAGS.filter((k) => allText.includes(k));
  let severity = 'low';
  let advice = 'I sintomi descritti sembrano lievi. Continua a monitorare e prenota una visita ambulatoriale nei prossimi giorni.';
  if (red.length) {
    severity = 'critical';
    advice = '⚠️ I sintomi riferiti sono compatibili con una condizione urgente. Contatta immediatamente il 112 o recati al pronto soccorso.';
  } else if (yellow.length >= 2) {
    severity = 'medium';
    advice = 'I sintomi meritano attenzione a breve termine. Ti consiglio un teleconsulto entro le prossime 12 ore.';
  } else if (yellow.length === 1) {
    severity = 'low-medium';
    advice = 'Sintomi da monitorare. Se peggiorano nelle prossime ore, prenota un teleconsulto.';
  }
  return {
    severity,
    reply: advice,
    flags: { red, yellow },
    session_id: 'wl-mock-' + Math.random().toString(36).slice(2, 10),
    _mock: true,
  };
}

// ------------------------------------------------------------
// Daily briefing (doctor home) — deterministic stats-based greeting.
// ------------------------------------------------------------

export function mockDailyBriefing({ appointments_today = 0, unread_messages = 0, alerts = 0 } = {}) {
  const hour = new Date().getHours();
  const salutation = hour < 12 ? 'Buongiorno' : hour < 18 ? 'Buon pomeriggio' : 'Buonasera';
  const lines = [];
  lines.push(`${salutation}. Hai **${appointments_today} appuntamenti** in agenda oggi.`);
  if (unread_messages > 0) lines.push(`📩 ${unread_messages} messaggi da leggere.`);
  if (alerts > 0) lines.push(`⚠️ ${alerts} alert IoT in sospeso da revisionare.`);
  if (!unread_messages && !alerts) lines.push('Nessuna notifica urgente — buona giornata!');
  return {
    text: lines.join(' '),
    generated_at: new Date().toISOString(),
    _mock: true,
  };
}

// ------------------------------------------------------------
// Chat AI — canned responder based on last user message keywords.
// ------------------------------------------------------------

const CHAT_INTENTS = [
  { re: /appunt|prenot|visit/i, reply: 'Puoi prenotare una visita dalla sezione Telemedicina. Ti serve un consulto orientativo o una visita specialistica?' },
  { re: /ricett|farmac|prescriz/i, reply: 'Le ricette elettroniche sono disponibili nella sezione Prescrizioni. Il medico può firmarle digitalmente con OTP.' },
  { re: /pagamen|prezz|isee/i, reply: 'Il prezzo dipende dal tuo ISEE. Vai su Telemedicina → Sconto ISEE per calcolare la tariffa personalizzata.' },
  { re: /famil|bundle|figli/i, reply: 'Puoi attivare il bundle famiglia per te e i tuoi familiari con uno sconto compreso tra il 10% e il 20%. Vai su Telemedicina → Bundle famiglia.' },
];

export function mockChatResponse({ message = '', history = [] } = {}) {
  const lower = String(message).toLowerCase();
  const hit = CHAT_INTENTS.find(({ re }) => re.test(lower));
  return {
    reply: hit ? hit.reply : 'Ricevuto. Ti ricordo che in modalità white-label questa risposta è generata localmente senza servizi cloud. Contatta la clinica per informazioni personalizzate.',
    session_id: history?.[0]?.session_id || 'wl-mock-' + Math.random().toString(36).slice(2, 10),
    _mock: true,
  };
}

// ------------------------------------------------------------
// Doctor Insights — deterministic derived stats.
// ------------------------------------------------------------

export function mockDoctorInsights() {
  return {
    highlights: [
      { icon: 'trend', label: 'Occupazione settimana', value: '72%', delta: '+8%' },
      { icon: 'star',  label: 'Rating medio',           value: '4.7', delta: '+0.2' },
      { icon: 'clock', label: 'Attesa media',           value: '3 min', delta: '-1 min' },
    ],
    suggestions: [
      'Considera di aprire 2 slot orientativi il venerdì mattina (alta domanda rilevata).',
      'Rispondi alle recensioni recenti: aumenta il rating di ~0.3 punti in 30 giorni.',
    ],
    _mock: true,
  };
}

// ------------------------------------------------------------
// Video promo — canned preset list without any external call.
// ------------------------------------------------------------

export function mockVideoPromoPresets() {
  return {
    presets: [
      { id: 'presentation', label: 'Presentazione studio', description: 'Un breve saluto ai tuoi pazienti.' },
      { id: 'service',      label: 'Nuovo servizio',       description: 'Presenta un nuovo esame o strumento.' },
      { id: 'family',       label: 'Salute in famiglia',   description: 'Mostra il valore del bundle famiglia.' },
      { id: 'custom',       label: 'Prompt personalizzato', description: 'Scrivi liberamente la scena.' },
    ],
    endpoint: 'local-mock',
    max_duration: 10,
    _mock: true,
  };
}

export function mockVideoPromoGenerate() {
  return {
    job_id: 'wl-mock-' + Math.random().toString(36).slice(2, 10),
    status: 'unavailable_in_whitelabel',
    message: 'La generazione video è disabilitata in modalità white-label locale. Configura una tua chiave Fal.ai o rimuovi la feature dalla UI.',
    _mock: true,
  };
}

// ------------------------------------------------------------
// Care Team AI (Fase 23) — decline score & family update, local-only.
// Deterministic heuristics so the panel is fully usable offline.
// ------------------------------------------------------------

const nowIso = () => new Date().toISOString();
const mockId = (p) => `${p}-${Math.random().toString(36).slice(2, 10)}`;

export function mockCareDeclineScore({ teamId = '', days = 14, scales = [] } = {}) {
  const n = Array.isArray(scales) ? scales.length : 0;
  const level = n >= 3 ? 3 : n >= 1 ? 2 : 1;
  const result = {
    livello_rischio: level,
    analisi_trend: n
      ? `Analisi locale su ${n} scale inserite negli ultimi ${days} giorni: andamento sostanzialmente stabile, nessun peggioramento rapido rilevato.`
      : `Nessuna scala clinica (NEWS2/Braden/Barthel) registrata negli ultimi ${days} giorni: valutazione basata solo su note e parametri disponibili.`,
    raccomandazione: level >= 3
      ? 'Rivalutare i parametri vitali entro 24 ore e condividere l\'esito con il medico di riferimento.'
      : 'Proseguire il monitoraggio ordinario e registrare le scale cliniche a cadenza settimanale.',
    indicatori_critici: level >= 3 ? ['Scale multiple nel periodo: verificare trend NEWS2'] : [],
    dati_usati: { vitali: 0, note: 0, scale: n, giorni: days },
  };
  return { report: { id: mockId('care-ai'), team_id: teamId, kind: 'decline_score', created_at: nowIso(), result, _mock: true } };
}

export function mockCareFamilyUpdate({ teamId = '', shift_notes = '' } = {}) {
  const text = (shift_notes || '').trim();
  const testo = text
    ? `Buongiorno,\n\noggi la giornata è trascorsa con serenità. Dalle note del turno: ${text.slice(0, 280)}${text.length > 280 ? '…' : ''}\n\nL'equipe continua a seguire con attenzione ogni aspetto del benessere quotidiano. Per qualsiasi domanda siamo a disposizione.\n\nUn caro saluto dal team di cura.`
    : 'Buongiorno,\n\noggi non sono state registrate note operative dal team. La giornata è trascorsa regolarmente e l\'equipe resta a disposizione per ogni necessità.\n\nUn caro saluto dal team di cura.';
  const result = { testo, fonte: text ? 'manual' : 'timeline', caratteri_input: text.length };
  return { report: { id: mockId('care-ai'), team_id: teamId, kind: 'family_update', created_at: nowIso(), result, _mock: true } };
}
